<script>
import { debounce } from '@chatwoot/utils';
import { useAlert } from 'dashboard/composables';

export default {
  name: 'PromotionsToolbar',

  props: {
    search: {
      type: String,
      default: '',
    },
    activeStatus: {
      type: [String, Boolean, null],
      default: null,
    },
    branch: {
      type: Object,
      default: null,
    },
    service: {
      type: [String, Number, null],
      default: null,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    isFilterActive: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update:search',
    'update:activeStatus',
    'update:branch',
    'update:service',
    'clear-filters'
  ],

  data() {
    return {
      debouncedUpdateSearch: null,
      localSearch: this.search,
    };
  },

  computed: {
    branchList() {
      let list = this.$store.state.branchV2.allBranches || [];
      let newList = list.map(b => ({ id: b.branch.id, name: b.branch.name }));
      return newList;
    },

    // Flatten service structure
    serviceOptions() {
      const raw = this.$store.state.serviceOptions?.allOptions || [];

      return raw.map(item => ({
        id: item.service?.id,
        name: item.service?.name,
        is_active: item.service?.is_active
      }));
    }
  },

  watch: {
    search(newVal) {
      this.localSearch = newVal;
    },
    localSearch(newVal) {
      this.debouncedUpdateSearch(newVal);
    },
    activeStatus(newVal) {
      console.log(`Received by toolbar: ${newVal}`);
    }
  },

  created() {
    this.debouncedUpdateSearch = debounce((value) => {
      this.$emit('update:search', value);
    }, 300);
  },

  methods: {
    handleActiveStatusChange(value) {
      let parsedValue;
      if (value === '') {
        parsedValue = null;
      } else {
        parsedValue = value;
      }
      console.log(`Emit to parent: ${parsedValue}`);
      this.$emit('update:activeStatus', parsedValue);
    },

    handleBranchChange(value) {
      this.$emit('update:branch', value);
    },

    handleServiceChange(value) {
      this.$emit('update:service', value || null);
    },

    handleClearFilters() {
      this.$emit('clear-filters');
    },

    async fetchServiceOptions() {
      try {
        await this.$store.dispatch('serviceOptions/fetchAllOptions');
      } catch (err) {
        console.error(err);
        useAlert(`Failed to fetch service options: ${err}`);
      }
    },
  },

  mounted() {
    this.fetchServiceOptions();
  },

  beforeUnmount() {
    this.debouncedUpdateSearch?.cancel?.();
  }
};
</script>

<template>
  <div class="w-full mb-4 flex flex-col md:flex-row gap-4 items-end">
    <!-- Search Input -->
    <div class="w-full md:flex-1">
      <label class="text-sm font-medium mb-1 block">Search</label>
      <input
        v-model="localSearch"
        placeholder="Search by name, description, or T&C..."
        class="border px-3 py-2 border-slate-200 rounded-md w-full mb-4"
      />
    </div>

    <!-- Active Status Filter -->
    <div class="w-full md:w-48">
      <label class="text-sm font-medium mb-1 block">Active Status</label>
      <div class="border h-12 rounded-md flex items-center mb-4">
        <select
          :value="activeStatus"
          @change="handleActiveStatusChange($event.target.value)"
          class="border-0 mb-0 w-full appearance-none"
          :disabled="isLoading"
        >
          <option :value="null">All</option>
          <option :value="true">Active Only</option>
          <option :value="false">Inactive Only</option>
        </select>
      </div>
    </div>

    <!-- Branch Filter -->
    <div class="w-full md:w-48">
      <label class="text-sm font-medium mb-1 block">Branch</label>
      <multiselect
        :value="branch"
        @input="handleBranchChange"
        :options="branchList"
        label="name"
        track-by="id"
        placeholder="All Branch"
      />
    </div>

    <!-- Service Filter -->
    <div class="w-full md:w-48">
      <label class="text-sm font-medium mb-1 block">Service</label>
      <div class="border h-12 rounded-md flex items-center mb-4">
        <select
          :value="service"
          @change="handleServiceChange($event.target.value)"
          class="border-0 mb-0 w-full appearance-none"
          :disabled="isLoading"
        >
          <option :value="null">All Services</option>

          <option
            v-for="svc in serviceOptions"
            :key="svc.id"
            :value="svc.id"
          >
            {{ svc.name }}
          </option>
        </select>
      </div>
    </div>

    <!-- Clear Filters Button -->
    <woot-button
      color-scheme="secondary"
      variant="smooth"
      size="small"
      @click="handleClearFilters"
      :disabled="!isFilterActive || isLoading"
      class="mb-4"
    >
      <fluent-icon icon="dismiss" size="16" class="mr-1" />
      Clear Filters
    </woot-button>
  </div>
</template>

<style scoped>
select {
  height: 38px;
}
</style>